import * as React from 'react';
import _ from 'lodash';
import useUpdateRef from '../../../hooks/useUpdateRef';
import type { Props as InputProps } from '../Input/Input';
import Input from '../Input/Input';
import Button from '../Button/Button';
import cn from '~modules/cn';

import SearchIcon from '../../../assets/icons/v2design/basics/search.svg';
import CloseIcon from '../../../assets/icons/v2design/basics/close.svg';

import './search-field.scss';

type Props = Omit<InputProps, 'ref'> & {
    inputRef?: React.Ref<HTMLInputElement>;
    debounce?: number;
    closeButtonSize?: 'large' | 'medium' | 'small';
    dataTestId?: string;
    containerStyle?: React.CSSProperties;
};

const SearchField = React.forwardRef<HTMLDivElement, Props>((props, ref): JSX.Element => {
    const inputProps = _.omit(props, ['className', 'debounce', 'inputRef', 'closeButtonSize', 'dataTestId', 'containerStyle']);
    const onChangeRef = useUpdateRef(props.onChange);
    const debouncedChange = React.useMemo(
        () => (onChangeRef.current && props.debounce ? _.debounce((e) => onChangeRef.current?.(e), props.debounce) : undefined),
        [props.debounce, onChangeRef]
    );
    const [value, setValue] = React.useState(props.value ?? '');
    const isFocusRef = React.useRef(false);
    React.useEffect(() => {
        setValue(props.value ?? '');
        debouncedChange?.cancel();
    }, [props.value, debouncedChange]);

    const clearButtonHandler = React.useCallback((e: React.MouseEvent) => {
        const input = e.currentTarget.previousElementSibling as HTMLInputElement;
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        nativeInputValueSetter?.call(input, '');
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }, []);

    return (
        <div
            className={cn('search-field', props.className, {
                'search-field--disabled': props.disabled,
                'search-field--has-value': !!props.value || !!props.defaultValue
            })}
            ref={ref}
            data-testid={props.dataTestId}
            style={props.containerStyle}
        >
            <SearchIcon className="search-field__search-icon" />
            <Input
                {...inputProps}
                value={value}
                ref={props.inputRef}
                data-testid="search-field-input"
                onFocus={(e) => {
                    isFocusRef.current = true;
                    props.onFocus?.(e);
                }}
                onChange={(e) => {
                    e.persist();
                    setValue(e.target.value);
                    if (props.debounce && e.target.value) {
                        debouncedChange?.(e);
                    } else {
                        debouncedChange?.cancel();
                        props.onChange?.(e);
                    }
                }}
                onBlur={(e) => {
                    isFocusRef.current = false;
                    debouncedChange?.flush();
                    props.onBlur?.(e);
                }}
            />
            <Button
                buttonSize={props.closeButtonSize ?? 'small'}
                buttonType="transparent"
                icon={<CloseIcon />}
                className="search-field__clear-button"
                data-testid="search-field-clear-button"
                disabled={props.disabled}
                onMouseDown={(e) => {
                    if (isFocusRef.current) {
                        // click might not register so in this case better to fire event here
                        clearButtonHandler(e);
                    }
                }}
                onClick={clearButtonHandler}
            />
        </div>
    );
});

SearchField.displayName = 'SearchField';

export default SearchField;
